import type { AnyFieldApi } from "@tanstack/solid-form";
import { AiOutlineLoading3Quarters } from "solid-icons/ai";
import { IoChevronDownOutline } from "solid-icons/io";
import {
  createDeferred,
  createResource,
  createSignal,
  For,
  Match,
  Show,
  Suspense,
  Switch,
} from "solid-js";
import { fetchSuggestedLocations } from "@/api/geo-code";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTitle,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/libs/cn";
import type { GeocodeAutocompleteProperties } from "@/types/geo-code";

export interface LocationPopoverProps {
  /**
   * The stored location, in the `"city, country"` shape this picker writes.
   * Shown on the trigger as-is: what the user reads is what gets saved.
   */
  value: string;
  onChange: (value: string) => void;
  /** Put on the trigger, so a `<label for>` and a focus-by-id both reach it. */
  id?: string;
  /** Merged into the trigger, for callers with their own field metrics. */
  class?: string;
  triggerRef?: (element: HTMLButtonElement) => void;
  /** Fires on every open *and* close, for callers that track the popup. */
  onOpenChange?: (open: boolean) => void;
}

/**
 * What a picked suggestion actually becomes.
 *
 * The normalised `"city, country"` shape needs both halves. Interpolating the
 * raw fields instead happily emits `", Italia"` or `"Torino, "` whenever the
 * geocoder omits one — a dangling-comma string that is perfectly saveable, and
 * that the trigger then reads back to the user verbatim, since the trigger
 * shows exactly what is stored. So an incomplete pair falls back to the
 * geocoder's own `formatted` label: not normalised, but a whole real place
 * rather than a fragment. Blank halves count as missing.
 */
const toStoredLocation = (
  location: GeocodeAutocompleteProperties,
): string | undefined => {
  const city = location.city?.trim();
  const country = location.country?.trim();
  if (city && country) return `${city}, ${country}`;
  return location.formatted?.trim() || undefined;
};

/**
 * The location picker, controlled: it owns the search box and the popup, the
 * caller owns the value.
 *
 * What it writes is `"city, country"` — the whole reason to reach for it over a
 * text input is that every location in the product ends up in the same shape.
 * The geocoder's own `formatted` string ("Torino, TO, Italia") is what the
 * *suggestion list* shows, and it is deliberately not what gets stored, except
 * as the fallback described on `toStoredLocation`.
 */
export const LocationPopover = (props: LocationPopoverProps) => {
  const { t } = useI18n();
  // Seeded from the current value, so opening the picker searches around where
  // the user already is instead of from a blank box.
  const [locationInput, setLocationInput] = createSignal<string>(props.value);
  const [open, setOpen] = createSignal(false);
  const deferredLocationInput = createDeferred(locationInput);
  const [suggestedLocations] = createResource(
    deferredLocationInput,
    fetchSuggestedLocations,
  );

  const handleOpenChange = (next: boolean) => {
    // Re-seed on every open, not just at construction. The caller can swap
    // `value` under a mounted picker — a profile query resolving into an
    // untouched TanStack form's defaults does exactly that — and the trigger
    // follows `props.value` while a once-seeded search box would not, leaving
    // the two disagreeing. Opening is also the only moment the box is read, so
    // re-seeding here needs no effect and no extra reactivity.
    if (next) setLocationInput(props.value);
    setOpen(next);
    props.onOpenChange?.(next);
  };

  const handleSelectLocation = (location: GeocodeAutocompleteProperties) => {
    const stored = toStoredLocation(location);
    // Nothing nameable came back. Writing `""` would wipe a location the user
    // already had and drop the trigger back to the placeholder with no
    // explanation, so leave both the value and the open picker alone.
    if (!stored) return;
    handleOpenChange(false);
    props.onChange(stored);
  };

  return (
    <div>
      <Popover
        open={open()}
        onOpenChange={handleOpenChange}
        gutter={10}
        sameWidth
      >
        <PopoverTrigger
          as={Button}
          ref={props.triggerRef}
          id={props.id}
          variant="input"
          class={cn("w-full min-w-50 gap-4 justify-between h-10", props.class)}
        >
          <Show
            when={props.value}
            fallback={
              <p class="truncate text-left text-muted-foreground">
                {t("common.location_placeholder")}
              </p>
            }
          >
            <p class="truncate text-left">{props.value}</p>
          </Show>
          {/* No `self-end`: the trigger is a fixed-height row with a
              single-line, truncating label, so bottom-aligning the chevron only
              drops it below the text it belongs to. The button's own
              `items-center` is the right answer. */}
          <IoChevronDownOutline
            class="w-5 h-5 transition-transform duration-200"
            classList={{
              "rotate-180": open(),
            }}
          />
        </PopoverTrigger>

        {/* A leaf: search box and suggestions open no other layer, so it may
            be `topLayer` — it sits inside the personal-data dialogs. */}
        <PopoverContent topLayer class="px-0 w-full">
          <div class="flex flex-col gap-2 px-4 py-2">
            <PopoverTitle>{t("common.location_title")}</PopoverTitle>
            <Input
              value={locationInput()}
              onInput={(e) => setLocationInput(e.target.value)}
            />
          </div>
          <div class="mt-2">
            <Suspense
              fallback={
                <AiOutlineLoading3Quarters
                  class="animate-spin text-xl w-10 h-10 mx-auto"
                  size={28}
                />
              }
            >
              <Switch>
                <Match when={suggestedLocations.error}>
                  <div>{t("common.error")}</div>
                </Match>
                <Match when={suggestedLocations()?.features}>
                  <Show when={suggestedLocations()?.features?.length === 0}>
                    <div class="px-4 py-2 text-center text-sm text-gray-500">
                      {t("common.no_results_found")}
                    </div>
                  </Show>
                  <ul class="max-h-50 overflow-y-auto">
                    <For each={suggestedLocations()?.features ?? []}>
                      {(feature) => (
                        <li
                          class="py-2 px-4 hover:bg-gray-100 cursor-pointer"
                          onClick={() =>
                            handleSelectLocation(feature.properties)
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              handleSelectLocation(feature.properties);
                            }
                          }}
                        >
                          {feature.properties.formatted}
                        </li>
                      )}
                    </For>
                  </ul>
                </Match>
                <Match when={!suggestedLocations()?.features}>
                  <Show when={locationInput() === ""}>
                    <div class="px-4 py-2 text-center text-sm text-gray-500">
                      {t("common.location_placeholder")}
                    </div>
                  </Show>
                </Match>
              </Switch>
            </Suspense>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

/**
 * The same picker, wired to a TanStack Form field.
 *
 * The hidden input is what puts the field's name and value in the DOM — the
 * trigger is a button and carries neither — and it is also where `handleBlur`
 * lives, so the field still knows it has been visited.
 *
 * **The id goes on the trigger, not on that input.** Every caller pairs this
 * with a `<FieldContainerLabel for={field.name}>`, which is a real `<label
 * for>`, and `input[type="hidden"]` is not a labelable element: pointing the
 * attribute there resolved to nothing, so the label was inert — clicking it
 * neither opened nor focused the picker. The trigger is a `<button>`, which is
 * labelable, so the same id makes the association work. Only one node may hold
 * it, hence the input keeps its `name` and gives up its `id`.
 */
export const LocationPopoverForm = (props: { fieldsApi: AnyFieldApi }) => (
  <div>
    <Input
      name={props.fieldsApi.name}
      value={props.fieldsApi.state.value ?? ""}
      type="hidden"
      onInput={props.fieldsApi.handleChange}
      onBlur={props.fieldsApi.handleBlur}
    />
    <LocationPopover
      id={props.fieldsApi.name}
      value={props.fieldsApi.state.value ?? ""}
      onChange={(value) => props.fieldsApi.handleChange(value)}
    />
  </div>
);
